// src/components/partners/DistributorList.jsx
import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Row, Col, Form, InputGroup, Alert, Badge } from 'react-bootstrap';
import { Search, Edit, Trash, RefreshCw, XCircle, Info, DollarSign } from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import { deleteDistributor, deleteMultipleDistributors, getAllDistributors } from '../../api/partner';

const DistributorList = ({ onAddDistributor, onEditDistributor, onViewDistributor, onManagePrices }) => {
    const { isAdmin, isManager } = useAuth();
    const [distributors, setDistributors] = useState([]);
    const [filteredDistributors, setFilteredDistributors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [selectedDistributors, setSelectedDistributors] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    const canModify = isAdmin || isManager;

    useEffect(() => {
        fetchDistributors();
    }, [refreshTrigger]);

    useEffect(() => {
        filterDistributors();
    }, [searchTerm, distributors]);

    useEffect(() => {
        // Réinitialiser la sélection quand les distributeurs filtrés changent
        setSelectedDistributors([]);
        setSelectAll(false);
    }, [filteredDistributors]);

    const fetchDistributors = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await getAllDistributors();
            setDistributors(Array.isArray(data) ? data : []);
            setFilteredDistributors(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Impossible de charger les distributeurs. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filterDistributors = () => {
        if (searchTerm.trim() === '') {
            setFilteredDistributors(distributors);
        } else {
            const filtered = distributors.filter(distributor =>
                distributor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (distributor.email && distributor.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (distributor.contractNumber && distributor.contractNumber.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            setFilteredDistributors(filtered);
        }
    };

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce distributeur ?')) {
            try {
                await deleteDistributor(id);
                setDistributors(distributors.filter(distributor => distributor.id !== id));
            } catch (err) {
                setError('Échec de la suppression du distributeur. Veuillez réessayer.');
                console.error(err);
            }
        }
    };

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        setSelectAll(isChecked);
        setSelectedDistributors(isChecked ? filteredDistributors.map(distributor => distributor.id) : []);
    };

    const handleSelectDistributor = (id) => {
        setSelectedDistributors(prev => {
            if (prev.includes(id)) {
                return prev.filter(distributorId => distributorId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleDeleteSelected = async () => {
        if (selectedDistributors.length === 0) return;

        if (window.confirm(`Êtes-vous sûr de vouloir supprimer les ${selectedDistributors.length} distributeurs sélectionnés ?`)) {
            try {
                await deleteMultipleDistributors(selectedDistributors);
                setDistributors(distributors.filter(distributor => !selectedDistributors.includes(distributor.id)));
                setSelectedDistributors([]);
                setSelectAll(false);
            } catch (err) {
                setError('Échec de la suppression des distributeurs sélectionnés. Veuillez réessayer.');
                console.error(err);
            }
        }
    };

    // Badge TVA réutilisé (tableau + cartes)
    const renderVatBadge = (distributor) =>
        distributor.vatApplicable ? (
            <Badge bg="success">Oui</Badge>
        ) : (
            <Badge bg="secondary">Non</Badge>
        );

    // Groupe d'actions réutilisé (tableau lg + cartes mobile)
    const renderActions = (distributor) => (
        <div className="d-flex gap-2 flex-wrap justify-content-end">
            <Button
                variant="outline-info"
                size="sm"
                onClick={() => onViewDistributor(distributor.id)}
                title="Détails"
            >
                <Info size={16} />
            </Button>
            {canModify && (
                <>
                    <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => onManagePrices(distributor.id)}
                        title="Gérer les prix"
                    >
                        <DollarSign size={16} />
                    </Button>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => onEditDistributor(distributor.id)}
                        title="Modifier"
                    >
                        <Edit size={16} />
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(distributor.id)}
                        title="Supprimer"
                    >
                        <Trash size={16} />
                    </Button>
                </>
            )}
        </div>
    );

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <Row className="align-items-center">
                    <Col>
                        <h5 className="mb-0">Liste des distributeurs</h5>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleRefresh}
                            title="Rafraîchir"
                        >
                            <RefreshCw size={16} />
                        </Button>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                <Row className="mb-3 g-2">
                    <Col xs={12} md={8}>
                        <InputGroup>
                            <InputGroup.Text>
                                <Search size={18} />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Rechercher par nom, email ou numéro de contrat..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col xs={12} md={4}>
                        {canModify && selectedDistributors.length > 0 && (
                            <Button
                                variant="danger"
                                onClick={handleDeleteSelected}
                                className="w-100"
                            >
                                <XCircle size={16} className="me-1" />
                                Supprimer sélectionnés ({selectedDistributors.length})
                            </Button>
                        )}
                    </Col>
                </Row>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                        <p className="mt-2">Chargement des distributeurs...</p>
                    </div>
                ) : filteredDistributors.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        Aucun distributeur trouvé
                    </div>
                ) : (
                    <>
                        {/* ≥ lg : tableau */}
                        <div className="table-responsive d-none d-lg-block">
                            <Table striped hover className="mb-0">
                                <thead>
                                    <tr>
                                        {canModify && (
                                            <th width="50">
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectAll}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                        )}
                                        <th>Nom</th>
                                        <th>Email</th>
                                        <th>N° Contrat</th>
                                        <th>TVA</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDistributors.map(distributor => (
                                        <tr key={distributor.id}>
                                            {canModify && (
                                                <td>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={selectedDistributors.includes(distributor.id)}
                                                        onChange={() => handleSelectDistributor(distributor.id)}
                                                    />
                                                </td>
                                            )}
                                            <td>{distributor.name}</td>
                                            <td>{distributor.email || '-'}</td>
                                            <td>{distributor.contractNumber || '-'}</td>
                                            <td>{renderVatBadge(distributor)}</td>
                                            <td>{renderActions(distributor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        {/* < lg : cartes */}
                        <div className="d-lg-none">
                            {canModify && (
                                <Form.Check
                                    type="checkbox"
                                    label="Tout sélectionner"
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    className="mb-2"
                                />
                            )}
                            {filteredDistributors.map(distributor => (
                                <div key={distributor.id} className="border rounded p-3 mb-2">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div className="d-flex align-items-start gap-2">
                                            {canModify && (
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedDistributors.includes(distributor.id)}
                                                    onChange={() => handleSelectDistributor(distributor.id)}
                                                    className="mt-1"
                                                />
                                            )}
                                            <div>
                                                <div className="fw-bold">{distributor.name}</div>
                                                <small className="text-muted">{distributor.email || '-'}</small>
                                            </div>
                                        </div>
                                        <div className="text-nowrap">
                                            <span className="text-muted small me-1">TVA</span>
                                            {renderVatBadge(distributor)}
                                        </div>
                                    </div>

                                    <div className="small mb-3">
                                        <span className="text-muted">N° Contrat : </span>
                                        {distributor.contractNumber || '-'}
                                    </div>

                                    {renderActions(distributor)}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default DistributorList;